import { FeatureFlag, useFeature } from '@utils/featureFlags'
import { Space, Tag, theme, Tooltip, Typography } from 'antd'
import { CSSProperties, PropsWithChildren, ReactNode } from 'react'

const { Text } = Typography

// Audience-neutral on purpose: a preview is "enabled by targeting, not for
// everyone yet", whoever the targeting includes (Supplément Bacon, admins…).
const PREVIEW_HINT =
  'Preview: this feature is only enabled for some accounts, not for everyone yet'

// antd purple-5, the dashed frame colour (the tag itself is the `purple` preset).
const FRAME_COLOR = '#9254de'

/** The purple "Preview" tag. Only rendered once a flag is known to be a preview. */
const PreviewBadge = () => (
  <Tooltip title={PREVIEW_HINT}>
    <Tag
      color="purple"
      variant="filled"
      style={{ marginInlineEnd: 0, fontWeight: 'normal' }}
    >
      Preview
    </Tag>
  </Tooltip>
)

/**
 * For places that are not JSX blocks (menu entries, tab labels, route lists):
 * `label` followed by the Preview tag when `preview` (from `useFeature`).
 */
const withPreviewTag = (label: ReactNode, preview: boolean): ReactNode =>
  preview ? (
    <Space size={8}>
      {label}
      <PreviewBadge />
    </Space>
  ) : (
    label
  )

interface Props {
  flag: FeatureFlag
  /**
   * How a PREVIEW is shown (a released feature is never marked):
   * - `section`: dashed purple frame with the label on its top edge, for
   *   blocks (page content, sections, cards);
   * - `inline`: the Preview tag right after the children, for buttons and
   *   other things that cannot be framed;
   * - `none` (default): children only.
   */
  preview?: 'section' | 'inline' | 'none'
  /** Rendered when the flag is off. */
  fallback?: ReactNode
  className?: string
  style?: CSSProperties
  /**
   * `section` only: colour of the pill the label sits in, which interrupts the
   * dashed line. The colour behind the frame: the page background by default,
   * white inside a card.
   */
  labelBackground?: string
}

/**
 * Feature flag switch + preview marking, nothing else: it knows no permission
 * (nest it in `<HasAccess>` when one is needed).
 *
 * - flag off: `fallback` (nothing by default);
 * - flag on and released (or a client admin, or previews hidden): the children,
 *   untouched - no wrapper, no spacing;
 * - flag on and a preview for this admin: see `preview`.
 */
const Feature = ({
  flag,
  preview: display = 'none',
  fallback = null,
  className,
  style,
  labelBackground,
  children,
}: PropsWithChildren<Props>) => {
  const { token } = theme.useToken()
  const { enabled, preview } = useFeature(flag)

  if (!enabled) return <>{fallback}</>
  if (!preview || display === 'none') return <>{children}</>

  if (display === 'inline') {
    return (
      <Space size={8}>
        {children}
        <PreviewBadge />
      </Space>
    )
  }

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        border: `1px dashed ${FRAME_COLOR}`,
        borderRadius: 8,
        padding: 16,
        background: 'transparent',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -12,
          left: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 8px',
          background: labelBackground ?? token.colorBgLayout,
          lineHeight: '22px',
        }}
      >
        <PreviewBadge />
        <Tooltip title={PREVIEW_HINT}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            not yet enabled for everyone
          </Text>
        </Tooltip>
      </div>
      {children}
    </div>
  )
}

export default Feature
export { PreviewBadge, withPreviewTag }
